import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Share2, BadgeCheck, Heart } from "lucide-react";
import { useAuth, api } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { appTexts } from "@/i18n/appTexts";
import { speak, stopSpeaking } from "@/lib/voice";

const EGG = "/robots/skins/egg.webp";
const EGG_CRACKED = "/robots/skins/egg_cracked.webp";
const BABY_VOICE = { pitch: 1.8, rate: 1.05 };

export default function Birth() {
  const { user, setUser } = useAuth();
  const { lang } = useLang();
  const t = appTexts[lang].birth;
  const navigate = useNavigate();
  const [phase, setPhase] = useState("arrival"); // arrival | askName | askUser | callMe | warmth | certificate
  const [showWake, setShowWake] = useState(false);
  const [subtitle, setSubtitle] = useState("");
  const [avatarName, setAvatarName] = useState("");
  const [userName, setUserName] = useState("");
  const [callMe, setCallMe] = useState("nombre");
  const [holding, setHolding] = useState(false);
  const [warmth, setWarmth] = useState(0);
  const [busy, setBusy] = useState(false);
  const holdRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => () => stopSpeaking(), []);

  const say = async (text) => {
    setSubtitle(text);
    await speak(text, lang, BABY_VOICE);
  };

  useEffect(() => {
    // egg descends, then asks to be touched — the tap is the user gesture that
    // UNLOCKS audio on mobile browsers (autoplay policy) for the whole experience
    const timer = setTimeout(() => setShowWake(true), 2200);
    return () => clearTimeout(timer);
  }, []);

  const wakeUp = () => {
    setShowWake(false);
    setPhase("askName"); // input visible immediately, speech runs in background
    say(t.hello);
  };

  const submitAvatarName = async () => {
    const name = avatarName.trim();
    if (!name) return;
    setPhase("askUser");
    await say(t.loveName.replace("{name}", name));
    await say(t.askUser);
  };

  const submitUserName = async () => {
    const name = userName.trim();
    if (!name) return;
    setPhase("callMe");
    await say(t.askCallMe.replace("{user}", name.split(" ")[0]));
  };

  const chooseCallMe = async (choice) => {
    setCallMe(choice);
    setPhase("warmth");
    const title = choice === "papa" ? "papá" : choice === "mama" ? "mamá" : userName.split(" ")[0];
    await say(t.thanksCallMe.replace("{title}", title));
    await say(t.askWarmth);
  };

  /* Press & hold 3s to warm the egg */
  const startHold = () => {
    if (phase !== "warmth" || doneRef.current) return;
    setHolding(true);
    holdRef.current = setInterval(() => {
      setWarmth((w) => {
        const next = Math.min(100, w + 3.4);
        if (next >= 100 && !doneRef.current) {
          doneRef.current = true;
          clearInterval(holdRef.current);
          completeBirth();
        }
        return next;
      });
    }, 100);
  };
  const stopHold = () => {
    setHolding(false);
    clearInterval(holdRef.current);
  };

  const completeBirth = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/birth/complete", {
        avatar_name: avatarName.trim(),
        user_name: userName.trim(),
        call_me: callMe,
      });
      setUser(data);
    } catch { /* names still shown locally */ }
    setBusy(false);
    await say(t.cracked.replace("{name}", avatarName.trim()));
    setPhase("certificate");
  };

  const shareCertificate = () => {
    const title = callMe === "papa" ? "papá" : callMe === "mama" ? "mamá" : "";
    const msg = t.waShare
      .replace("{avatar}", avatarName.trim())
      .replace("{title}", title ? ` (${t.iAmIts} ${title})` : "");
    window.open(`https://wa.me/?text=${encodeURIComponent(`${msg} ${window.location.origin}/register?ref=${user?.referral_code || ""}`)}`, "_blank", "noopener");
  };

  const finish = () => {
    stopSpeaking();
    navigate("/dashboard", { replace: true });
    setTimeout(() => {
      if (window.location.pathname !== "/dashboard") window.location.href = "/dashboard";
    }, 1500);
  };

  const ownerTitle = callMe === "papa" ? "Papá" : callMe === "mama" ? "Mamá" : userName.split(" ")[0];

  return (
    <div className="min-h-screen bg-[#03030A] text-white flex flex-col items-center justify-center px-6 relative overflow-hidden" data-testid="birth-page">
      <div className="absolute top-[-15%] left-[-10%] w-[480px] h-[480px] bg-[#FF6EC7] blur-[170px] opacity-10 rounded-full pointer-events-none" />
      <div className="absolute bottom-[-15%] right-[-10%] w-[480px] h-[480px] bg-[#00F5FF] blur-[170px] opacity-10 rounded-full pointer-events-none" />

      <AnimatePresence mode="wait">
        {phase === "certificate" ? (
          /* ===== DIGITAL BIRTH CERTIFICATE ===== */
          <motion.div key="cert" initial={{ opacity: 0, y: 40, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: "spring", duration: 0.8 }} className="w-full max-w-md" data-testid="birth-certificate">
            <div className="relative bg-gradient-to-b from-[#0F0F1A] to-[#141428] border-2 border-[#FFD700]/40 rounded-[2rem] p-8 text-center shadow-[0_0_50px_rgba(255,215,0,0.15)] overflow-hidden">
              <div className="absolute -top-14 -right-14 w-40 h-40 bg-[#FFD700] blur-[90px] opacity-15 pointer-events-none" />
              <p className="text-[9px] font-bold uppercase tracking-[0.35em] text-[#FFD700] mb-1">{t.certTitle}</p>
              <p className="text-[8px] uppercase tracking-[0.2em] text-white/30 mb-5">AVATAR 4 U · AvatarNet</p>
              <motion.img
                src={EGG_CRACKED} alt="born"
                animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="w-28 h-28 object-contain mx-auto mb-4 drop-shadow-[0_0_25px_rgba(255,215,0,0.4)]"
              />
              <h1 className="font-heading font-black text-2xl text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#FF6EC7] mb-1" data-testid="cert-avatar-name">{avatarName}</h1>
              <p className="text-xs text-white/60 mb-4">{t.certBornTo} <span className="font-black text-white">{userName}</span>{callMe !== "nombre" && <span className="text-[#FF6EC7] font-black"> · {ownerTitle}</span>}</p>
              <div className="flex items-center justify-center gap-4 text-[10px] text-white/40 mb-6">
                <span>📅 {new Date().toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { day: "numeric", month: "long", year: "numeric" })}</span>
                <span className="inline-flex items-center gap-1 text-[#00FFAA]"><BadgeCheck size={12} /> {t.certSeal}</span>
              </div>
              <div className="flex flex-col gap-2.5">
                <button data-testid="cert-share-btn" onClick={shareCertificate}
                  className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-white bg-[#25D366] shadow-[0_0_20px_rgba(37,211,102,0.4)]">
                  <Share2 size={13} /> {t.certShare}
                </button>
                <button data-testid="cert-continue-btn" onClick={finish}
                  className="w-full py-3.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#05050A] bg-gradient-to-r from-[#FFD700] to-[#FF6EC7] shadow-[0_0_20px_rgba(255,215,0,0.4)]">
                  {t.certContinue}
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="egg" className="flex flex-col items-center text-center max-w-md w-full">
            {/* THE EGG */}
            <motion.div
              initial={{ y: -300, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 2, ease: "easeOut" }}
              className="relative flex items-center justify-center mb-6"
            >
              {/* heartbeat rings */}
              <motion.span
                animate={{ scale: [1, 1.25, 1], opacity: [0.45, 0, 0.45] }}
                transition={{ duration: holding ? 0.7 : 1.6, repeat: Infinity, ease: "easeOut" }}
                className="absolute w-52 h-60 rounded-[50%] border-2"
                style={{ borderColor: holding ? "rgba(255,110,199,0.7)" : "rgba(0,245,255,0.4)" }}
              />
              <motion.div
                animate={{ scale: holding ? [1, 1.06, 1] : [1, 1.02, 1] }}
                transition={{ duration: holding ? 0.7 : 1.6, repeat: Infinity, ease: "easeInOut" }}
                onMouseDown={startHold} onMouseUp={stopHold} onMouseLeave={stopHold}
                onTouchStart={startHold} onTouchEnd={stopHold}
                className={`relative select-none ${phase === "warmth" ? "cursor-pointer" : ""}`}
                data-testid="birth-egg"
              >
                <img
                  src={warmth >= 100 ? EGG_CRACKED : EGG}
                  alt="Your avatar egg"
                  draggable={false}
                  className="w-44 h-44 object-contain transition-all duration-500"
                  style={{ filter: `drop-shadow(0 0 ${20 + warmth * 0.4}px rgba(255,${Math.round(110 + warmth)},199,${0.35 + warmth * 0.005}))` }}
                />
                {holding && (
                  <motion.span initial={{ scale: 0 }} animate={{ scale: [0, 1.2, 1] }} className="absolute -top-2 -right-2">
                    <Heart size={26} className="text-[#FF6EC7] fill-[#FF6EC7]/60" />
                  </motion.span>
                )}
              </motion.div>
            </motion.div>

            {/* Warmth bar */}
            {phase === "warmth" && (
              <div className="w-56 h-2.5 rounded-full bg-white/5 border border-white/10 overflow-hidden mb-5" data-testid="warmth-bar">
                <div className="h-full rounded-full bg-gradient-to-r from-[#FF6EC7] to-[#FFD700] transition-all duration-150 shadow-[0_0_12px_rgba(255,110,199,0.6)]" style={{ width: `${warmth}%` }} />
              </div>
            )}

            {/* Subtitle bubble */}
            <AnimatePresence mode="wait">
              <motion.p
                key={subtitle}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="text-sm text-white/85 leading-relaxed bg-white/5 border border-[#FF6EC7]/25 rounded-2xl px-5 py-4 min-h-[60px] w-full"
                data-testid="birth-subtitle"
              >
                {subtitle || "..."}
              </motion.p>
            </AnimatePresence>

            {/* Wake button — the audio-unlocking gesture */}
            {phase === "arrival" && showWake && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="birth-wake-btn"
                onClick={wakeUp}
                className="mt-5 inline-flex items-center gap-3 px-9 py-5 rounded-full font-heading font-black text-sm uppercase tracking-[0.12em] bg-gradient-to-r from-[#FF6EC7] to-[#B026FF] text-white shadow-[0_0_35px_rgba(255,110,199,0.5)] hover:shadow-[0_0_55px_rgba(255,110,199,0.8)] hover:-translate-y-1 transition-all animate-neon-pulse"
              >
                🥚 {t.wakeBtn}
              </motion.button>
            )}

            {/* Inputs per phase */}
            {phase === "askName" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-2 w-full mt-5">
                <input data-testid="birth-avatar-name-input" value={avatarName} maxLength={30}
                  onChange={(e) => setAvatarName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitAvatarName()}
                  placeholder={t.namePlaceholder} autoFocus
                  className="flex-1 bg-white/5 border border-[#FF6EC7]/35 rounded-full px-5 py-3.5 text-sm outline-none focus:border-[#FF6EC7]" />
                <button data-testid="birth-avatar-name-send" onClick={submitAvatarName} disabled={!avatarName.trim()}
                  className="w-12 h-12 rounded-full flex items-center justify-center bg-[#FF6EC7] shadow-[0_0_18px_rgba(255,110,199,0.5)] disabled:opacity-40 shrink-0">
                  <Send size={16} />
                </button>
              </motion.div>
            )}
            {phase === "askUser" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-2 w-full mt-5">
                <input data-testid="birth-user-name-input" value={userName} maxLength={40}
                  onChange={(e) => setUserName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitUserName()}
                  placeholder={t.userPlaceholder} autoFocus
                  className="flex-1 bg-white/5 border border-[#00F5FF]/35 rounded-full px-5 py-3.5 text-sm outline-none focus:border-[#00F5FF]" />
                <button data-testid="birth-user-name-send" onClick={submitUserName} disabled={!userName.trim()}
                  className="w-12 h-12 rounded-full flex items-center justify-center bg-[#00F5FF] text-[#05050A] shadow-[0_0_18px_rgba(0,245,255,0.5)] disabled:opacity-40 shrink-0">
                  <Send size={16} />
                </button>
              </motion.div>
            )}
            {phase === "callMe" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-wrap justify-center gap-2.5 mt-5" data-testid="callme-options">
                {[
                  { id: "nombre", label: userName.split(" ")[0] || t.byName },
                  { id: "papa", label: "Papá 👨" },
                  { id: "mama", label: "Mamá 👩" },
                ].map((o) => (
                  <button key={o.id} data-testid={`callme-${o.id}`} onClick={() => chooseCallMe(o.id)}
                    className="px-6 py-3.5 rounded-full text-xs font-bold border border-[#FF6EC7]/40 bg-[#FF6EC7]/8 text-white hover:bg-[#FF6EC7]/20 hover:-translate-y-0.5 transition-all">
                    {o.label}
                  </button>
                ))}
              </motion.div>
            )}
            {phase === "warmth" && warmth < 100 && (
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF6EC7] mt-4 animate-pulse" data-testid="warmth-hint">
                {t.holdHint}
              </p>
            )}
            {busy && <p className="text-[10px] text-white/30 mt-3">...</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
